<template>
  <div class="dresscode">
    <section class="dresscode__hero">
      <div class="dresscode__hero-overlay" />
      <div class="dresscode__hero-content">
        <h1>Dress code</h1>
      </div>
    </section>

    <section class="dresscode__content">
      <div class="dresscode__inner">
        <!-- Cérémonie -->
        <div class="dresscode__section">
          <h2>Dress code &mdash; Cérémonie</h2>
          <p class="dresscode__subtitle">
            Samedi 13 juin
          </p>

          <div class="dresscode__palette">
            <div
              v-for="(color, i) in paletteCeremonie"
              :key="color.hex"
              class="dresscode__swatch"
              :class="`dresscode__swatch--v${(i % 3) + 1}`"
              :style="{ backgroundColor: color.hex }"
            />
          </div>

          <p class="dresscode__hint">
            Ces couleurs sont données à titre indicatif pour les tons &mdash; pas d'inquiétude si ce n'est pas exactement celle-ci que vous portez&nbsp;!
          </p>

          <p class="dresscode__intro">
            Nous avons choisi une palette colorée et nous serions très touchés qu'elle soit respectée le Jour J&#10024;
          </p>
          <p class="dresscode__intro">
            Rien d'obligatoire évidemment, mais si vous avez envie de jouer le jeu, nous pensons que les photos seront encore plus belles.
          </p>

          <div class="dresscode__tips">
            <p>
              <strong>Pour les femmes</strong>, robes, ensembles ou accessoires dans les tons de la palette.
            </p>
            <p>
              <strong>Pour les hommes</strong>, nous savons qu'il n'est pas évident de respecter ces couleurs, porter un costume (par exemple bleu marine) sera parfait, avec un petit rappel de couleur de la palette sur la cravate, le n&oelig;ud papillon, la boutonnière, la ceinture, le mouchoir&hellip;
            </p>
          </div>

          <p class="dresscode__note">
            Le blanc et ses nuances sont réservés à la mariée&nbsp;: le blanc cassé, le crème, le blanc lin, le blanc argile, le blanc ivoire, le blanc écru, etc.
          </p>
        </div>

        <div class="dresscode__divider" />

        <!-- Brunch -->
        <div class="dresscode__section">
          <h2>Dress code &mdash; Brunch</h2>
          <p class="dresscode__subtitle">
            Dimanche 14 juin
          </p>

          <p class="dresscode__text">
            Le lendemain, on inverse les rôles&nbsp;!
          </p>

          <div class="dresscode__rules">
            <div class="dresscode__rule">
              <div class="dresscode__rule-label">
                Vous
              </div>
              <p>On vous imagine en blanc ou tons très clairs</p>
              <div class="dresscode__mini-palette">
                <div
                  v-for="(color, i) in paletteBrunch"
                  :key="color.hex"
                  class="dresscode__mini-swatch"
                  :class="`dresscode__swatch--v${(i % 3) + 1}`"
                  :style="{ backgroundColor: color.hex }"
                />
              </div>
            </div>
            <div class="dresscode__rule">
              <div class="dresscode__rule-label">
                Nous
              </div>
              <p>On portera les couleurs de la palette</p>
              <div class="dresscode__mini-palette">
                <div
                  v-for="(color, i) in paletteCeremonie"
                  :key="color.hex"
                  class="dresscode__mini-swatch"
                  :class="`dresscode__swatch--v${(i % 3) + 1}`"
                  :style="{ backgroundColor: color.hex }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
const paletteCeremonie = [
  { hex: "#FEBB96", name: "Peach" },
  { hex: "#ECA240", name: "Golden hour" },
  { hex: "#CC5943", name: "Copper" },
  { hex: "#F09467", name: "Apricot" },
  { hex: "#F0A79A", name: "Blush" },
  { hex: "#A4BCC5", name: "Misty blue" },
  { hex: "#889987", name: "Sage" },
]

const paletteBrunch = [
  { hex: "#FFFFFF", name: "Blanc" },
  { hex: "#FFFFF0", name: "Ivoire" },
  { hex: "#F5F5DC", name: "Crème" },
  { hex: "#F5F0E1", name: "Blanc cassé" },
  { hex: "#F0E6D3", name: "Beige clair" },
]
</script>

<style lang="scss" scoped>
@import "../base/mixins";

.dresscode__hero {
  position: relative;
  height: 50vh;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-image: url('../images/IMG-20221226-WA0042.jpg');
  background-size: cover;
  background-position: center;

  @media (max-width: $bp-mobile) {
    max-height: 300px;
    min-height: 300px;
  }
}

.dresscode__hero-overlay {
  position: absolute;
  inset: 0;
  background: rgba($color-text-primary, 0.3);
}

.dresscode__hero-content {
  position: relative;
  z-index: 1;

  h1 {
    color: $color-white;
    font-weight: 300;
    letter-spacing: 0.05em;
  }
}

.dresscode__content {
  @include section-padding;
}

.dresscode__inner {
  @include container;
  text-align: center;
}

.dresscode__section {
  h2 {
    margin-bottom: $spacing-element-sm;
  }
}

.dresscode__subtitle {
  @include body-text;
  font-size: 0.8rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: $color-text-secondary;
  margin-bottom: $spacing-block;
}

.dresscode__palette {
  display: flex;
  justify-content: center;
  gap: $spacing-element;
  margin-bottom: $spacing-block;
  flex-wrap: wrap;
}

.dresscode__swatch {
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s;

  @media (min-width: $bp-tablet) {
    width: 120px;
    height: 120px;
  }

  &:hover {
    transform: scale(1.08);
  }

  // 3 paint splatter variants
  &--v1 {
    border-radius: 50% 40% 55% 45% / 45% 55% 40% 50%;
  }

  &--v2 {
    border-radius: 45% 55% 40% 50% / 55% 40% 50% 45%;
  }

  &--v3 {
    border-radius: 55% 45% 50% 40% / 40% 50% 45% 55%;
  }
}

.dresscode__hint {
  @include body-text;
  font-size: 0.85rem;
  font-style: italic;
  color: $color-text-secondary;
  max-width: 560px;
  margin: 0 auto $spacing-block;
}

.dresscode__intro {
  @include body-text;
  font-size: 1.05rem;
  max-width: 600px;
  margin: 0 auto $spacing-element;
}

.dresscode__tips {
  max-width: 600px;
  margin: $spacing-block auto;
  text-align: left;

  p {
    @include body-text;
    font-size: 0.95rem;
    margin-bottom: $spacing-element;
  }
}

.dresscode__note {
  @include body-text;
  font-size: 0.9rem;
  color: $color-text-secondary;
  max-width: 500px;
  margin: 0 auto;
  font-style: italic;
  font-family: $font-heading;
}

.dresscode__divider {
  width: 60px;
  height: 1px;
  background-color: $color-accent;
  margin: $spacing-block-lg auto;
}

.dresscode__text {
  @include body-text;
  font-size: 1.05rem;
  margin-bottom: $spacing-block;
}

.dresscode__rules {
  display: grid;
  gap: $spacing-block;
  margin-bottom: $spacing-block;

  @media (min-width: $bp-tablet) {
    grid-template-columns: 1fr 1fr;
    gap: $spacing-block;
  }
}

.dresscode__rule {
  padding: $spacing-block;
  background-color: $color-bg-secondary;

  p {
    @include body-text;
    font-size: 0.95rem;
  }
}

.dresscode__rule-label {
  @include heading;
  font-size: 1.1rem;
  font-weight: 800;
  margin-bottom: $spacing-element-sm !important;
}

.dresscode__mini-palette {
  display: flex;
  justify-content: center;
  gap: $spacing-element-sm;
  margin-top: $spacing-element;
  flex-wrap: wrap;
}

.dresscode__mini-swatch {
  width: 44px;
  height: 44px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: transform 0.3s;

  &:hover {
    transform: scale(1.12);
  }
}
</style>
